import { FEATURE_CODE, PRODUCT_CODE } from '../constants';
import {
  CAD_FILES_QUESTION,
  CLIENT_ACCESS_QUESTION,
  REGULATED_DATA_QUESTION,
  TEAM_SIZE_QUESTION,
} from './questions';
import { PROFILE_CATEGORY, type WorkspaceProfile } from './types';

/**
 * Perfiles de ingeniería, tecnología y construcción.
 *
 * Comparten un patrón: el trabajo se organiza por proyecto/obra, los planos y
 * memorias se revisan varias veces (de ahí el peso del historial de versiones)
 * y buena parte de la documentación se entrega a terceros — cliente,
 * interventoría o autoridad — lo que hace imprescindibles los enlaces con
 * caducidad y el registro de accesos.
 */
export const ENGINEERING_PROFILES: WorkspaceProfile[] = [
  {
    code: 'CIVIL_ENGINEERING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería civil',
    description: 'Proyectos de obra con planos versionados, memorias de cálculo y bitácora.',
    icon: '🏗️',
    sortOrder: 110,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          {
            name: 'Planos',
            children: [
              { name: 'Arquitectónicos' },
              { name: 'Estructurales' },
              { name: 'Hidrosanitarios' },
              { name: 'Eléctricos' },
            ],
          },
          { name: 'Memorias de cálculo' },
          {
            name: 'Estudios',
            children: [{ name: 'Suelos' }, { name: 'Topografía' }, { name: 'Impacto ambiental' }],
          },
          { name: 'Presupuestos y APU' },
          { name: 'Bitácora de obra' },
          { name: 'Fotografías de avance' },
        ],
      },
      { name: 'Licencias y permisos' },
      { name: 'Actas y minutas' },
      { name: 'Informes de interventoría' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'site', label: 'Obra', type: 'text', appliesTo: 'FOLDER' },
      { key: 'client', label: 'Cliente / Contratante', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Anteproyecto', 'Diseño', 'Licitación', 'Construcción', 'Entrega', 'Postventa'],
        appliesTo: 'BOTH',
      },
      {
        key: 'discipline',
        label: 'Disciplina',
        type: 'select',
        options: ['Estructural', 'Hidráulica', 'Sanitaria', 'Eléctrica', 'Vías', 'Geotecnia'],
        appliesTo: 'FILE',
      },
      { key: 'drawing_revision', label: 'Revisión del plano', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'project_types',
        label: '¿Qué tipo de proyectos manejas?',
        type: 'multiselect',
        options: [
          { value: 'buildings', label: 'Edificaciones' },
          { value: 'roads', label: 'Vías e infraestructura' },
          { value: 'hydraulic', label: 'Obras hidráulicas' },
          { value: 'structural', label: 'Cálculo estructural' },
          { value: 'supervision', label: 'Interventoría / Supervisión' },
        ],
      },
      CAD_FILES_QUESTION,
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['dwg', 'dxf', 'pdf', 'xlsx', 'ifc', 'rvt', 'jpg'],
  },
  {
    code: 'ARCHITECTURE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Arquitectura',
    description: 'Del levantamiento al proyecto ejecutivo, con renders y entregas a cliente.',
    icon: '📐',
    sortOrder: 120,
    terminology: {
      workspace: 'Estudio',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Levantamiento' },
          { name: 'Anteproyecto' },
          { name: 'Proyecto ejecutivo' },
          { name: 'Planos' },
          { name: 'Renders y visualización' },
          { name: 'Especificaciones' },
        ],
      },
      { name: 'Permisos y licencias' },
      { name: 'Presentaciones a cliente' },
      { name: 'Fotografías de obra' },
      { name: 'Biblioteca de recursos' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.CUSTOM_BRANDING,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'typology',
        label: 'Tipología',
        type: 'select',
        options: [
          'Residencial',
          'Comercial',
          'Institucional',
          'Industrial',
          'Urbanismo',
          'Interiorismo',
        ],
        appliesTo: 'FOLDER',
      },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Levantamiento', 'Anteproyecto', 'Ejecutivo', 'Obra', 'Entrega'],
        appliesTo: 'BOTH',
      },
      { key: 'scale', label: 'Escala', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CAD_FILES_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['dwg', 'pdf', 'skp', 'jpg', 'png', 'pptx', 'rvt'],
  },
  {
    code: 'CONSTRUCTION',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Construcción y contratistas',
    description: 'Obra en ejecución: contratos, actas de avance, calidad y seguridad.',
    icon: '🚧',
    sortOrder: 130,
    terminology: {
      workspace: 'Constructora',
      folder: 'Obra',
      folderPlural: 'Obras',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Obras',
        children: [
          { name: 'Contratos y adiciones' },
          { name: 'Planos aprobados' },
          { name: 'Programación y cronograma' },
          { name: 'Actas de avance' },
          { name: 'Fotografías de obra' },
          { name: 'Calidad y ensayos' },
          { name: 'Seguridad en obra' },
          { name: 'Bitácora' },
        ],
      },
      { name: 'Licitaciones y propuestas' },
      { name: 'Subcontratistas' },
      { name: 'Compras y suministros' },
      { name: 'Facturación y cobros' },
      { name: 'Liquidación y entrega' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
    ],
    metadataFields: [
      { key: 'work_code', label: 'Código de obra', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Contratante', type: 'text', appliesTo: 'FOLDER' },
      { key: 'contract_number', label: 'Número de contrato', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Licitación', 'Contratación', 'Ejecución', 'Liquidación', 'Postventa'],
        appliesTo: 'BOTH',
      },
      { key: 'progress_cut', label: 'Corte / Acta parcial', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'work_types',
        label: '¿Qué tipo de obra ejecutas?',
        type: 'multiselect',
        options: [
          { value: 'buildings', label: 'Edificación' },
          { value: 'roads', label: 'Infraestructura vial' },
          { value: 'hydraulic', label: 'Obras hidráulicas' },
          { value: 'utilities', label: 'Redes y servicios' },
          { value: 'remodeling', label: 'Remodelación y acabados' },
        ],
      },
      {
        key: 'public_tenders',
        label: '¿Participas en licitaciones públicas?',
        type: 'boolean',
        helpText: 'Prepararemos una carpeta específica para propuestas y anexos.',
      },
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'xlsx', 'dwg', 'jpg', 'docx'],
  },
  {
    code: 'ELECTRICAL_ENGINEERING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería eléctrica',
    description: 'Unifilares, cálculos, protecciones y certificaciones de instalaciones.',
    icon: '⚡',
    sortOrder: 140,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Diagramas unifilares' },
          { name: 'Planos eléctricos' },
          { name: 'Memorias de cálculo' },
          { name: 'Tableros y protecciones' },
          { name: 'Estudios (cortocircuito, coordinación, calidad de energía)' },
          { name: 'Puesta en marcha' },
        ],
      },
      { name: 'Certificaciones y ensayos' },
      { name: 'Mantenimiento e inspecciones' },
      { name: 'Normativa y catálogos' },
      { name: 'Contratos y actas' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'voltage_level',
        label: 'Nivel de tensión',
        type: 'select',
        options: ['Baja tensión', 'Media tensión', 'Alta tensión'],
        appliesTo: 'BOTH',
      },
      {
        key: 'installation_type',
        label: 'Tipo de instalación',
        type: 'select',
        options: ['Residencial', 'Comercial', 'Industrial', 'Subestación', 'Solar / Renovables'],
        appliesTo: 'FOLDER',
      },
      { key: 'drawing_revision', label: 'Revisión del plano', type: 'text', appliesTo: 'FILE' },
      {
        key: 'certificate_number',
        label: 'Número de certificación',
        type: 'text',
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'electrical_scope',
        label: '¿En qué trabajas principalmente?',
        type: 'multiselect',
        options: [
          { value: 'design', label: 'Diseño de instalaciones' },
          { value: 'substations', label: 'Subestaciones y potencia' },
          { value: 'automation', label: 'Automatización y control' },
          { value: 'renewables', label: 'Energías renovables' },
          { value: 'certification', label: 'Certificación e inspección' },
          { value: 'maintenance', label: 'Mantenimiento eléctrico' },
        ],
      },
      REGULATED_DATA_QUESTION,
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['dwg', 'dxf', 'pdf', 'xlsx', 'docx'],
    complianceNote:
      'Las certificaciones de instalaciones eléctricas suelen exigir conservar las evidencias durante años. Revisa la retención de papelera y el historial de versiones de tu plan.',
  },
  {
    code: 'MECHANICAL_INDUSTRIAL',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería mecánica e industrial',
    description: 'Planos de fabricación, procesos, mantenimiento y control de calidad.',
    icon: '⚙️',
    sortOrder: 150,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Planos de fabricación' },
          { name: 'Modelos 3D' },
          { name: 'Memorias de cálculo' },
          { name: 'Hojas de proceso' },
        ],
      },
      {
        name: 'Producción',
        children: [{ name: 'Órdenes de trabajo' }, { name: 'Control de calidad' }],
      },
      {
        name: 'Mantenimiento',
        children: [
          { name: 'Preventivo' },
          { name: 'Correctivo' },
          { name: 'Hojas de vida de equipos' },
        ],
      },
      { name: 'Proveedores y compras' },
      { name: 'Normas y catálogos' },
      { name: 'Seguridad industrial' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'equipment_code', label: 'Código de equipo', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Diseño', 'Prototipo', 'Fabricación', 'Montaje', 'Operación'],
        appliesTo: 'BOTH',
      },
      { key: 'material', label: 'Material', type: 'text', appliesTo: 'FILE' },
      { key: 'drawing_revision', label: 'Revisión del plano', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'mechanical_scope',
        label: '¿A qué te dedicas principalmente?',
        type: 'multiselect',
        options: [
          { value: 'design', label: 'Diseño mecánico' },
          { value: 'manufacturing', label: 'Fabricación y metalmecánica' },
          { value: 'maintenance', label: 'Mantenimiento industrial' },
          { value: 'processes', label: 'Procesos y mejora continua' },
          { value: 'quality', label: 'Calidad y metrología' },
          { value: 'hvac', label: 'HVAC y climatización' },
        ],
      },
      CAD_FILES_QUESTION,
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['dwg', 'dxf', 'step', 'stp', 'igs', 'stl', 'pdf', 'xlsx'],
  },
  {
    code: 'TELECOM_ENGINEERING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería de telecomunicaciones',
    description:
      'Diseño de red, sitios, fibra y radiofrecuencia con documentación por emplazamiento.',
    icon: '📡',
    sortOrder: 160,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Diseño de red' },
          { name: 'Radiofrecuencia y radioenlaces' },
          { name: 'Transmisión y fibra óptica' },
          { name: 'Planos y diagramas' },
          { name: 'Memorias técnicas' },
        ],
      },
      {
        name: 'Sitios y emplazamientos',
        children: [
          { name: 'Documentación legal del sitio' },
          { name: 'Fotografías' },
          { name: 'Actas de instalación' },
        ],
      },
      {
        name: 'Mediciones y pruebas',
        children: [{ name: 'Drive test' }, { name: 'OTDR y certificación de fibra' }],
      },
      { name: 'Permisos y licencias' },
      { name: 'Inventario de equipos' },
      { name: 'Operación y mantenimiento' },
      { name: 'Contratos y órdenes de servicio' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
    ],
    metadataFields: [
      { key: 'site_id', label: 'Código de sitio', type: 'text', appliesTo: 'BOTH' },
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'operator', label: 'Operador / Cliente', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'technology',
        label: 'Tecnología',
        type: 'select',
        options: [
          '2G / 3G',
          '4G LTE',
          '5G',
          'Fibra óptica / FTTH',
          'Microondas / Radioenlace',
          'Satelital',
          'WiFi',
          'Redes LAN / WAN',
        ],
        appliesTo: 'BOTH',
      },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Diseño', 'Permisos', 'Implementación', 'Pruebas', 'Operación'],
        appliesTo: 'BOTH',
      },
      { key: 'coordinates', label: 'Coordenadas', type: 'text', appliesTo: 'FOLDER' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'telecom_scope',
        label: '¿En qué trabajas principalmente?',
        type: 'multiselect',
        options: [
          { value: 'network_design', label: 'Diseño y planificación de red' },
          { value: 'rollout', label: 'Implementación y rollout' },
          { value: 'fiber', label: 'Fibra óptica / FTTH' },
          { value: 'rf', label: 'Radiofrecuencia y radioenlaces' },
          { value: 'testing', label: 'Mediciones y pruebas' },
          { value: 'maintenance', label: 'Operación y mantenimiento' },
        ],
      },
      {
        key: 'manages_sites',
        label: '¿Gestionas documentación por sitio o emplazamiento?',
        type: 'boolean',
        helpText: 'Crearemos una estructura por sitio con fotografías y actas.',
      },
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'dwg', 'xlsx', 'kmz', 'kml', 'csv', 'jpg', 'zip'],
  },
  {
    code: 'SOFTWARE_IT',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería de sistemas y TI',
    description: 'Documentación técnica, entregables, infraestructura y soporte.',
    icon: '💻',
    sortOrder: 170,
    terminology: {
      workspace: 'Equipo',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Requerimientos' },
          { name: 'Arquitectura y diseño' },
          { name: 'Entregables' },
          { name: 'Pruebas y QA' },
          { name: 'Manuales y documentación' },
        ],
      },
      { name: 'Clientes' },
      {
        name: 'Infraestructura',
        children: [
          { name: 'Diagramas de red' },
          { name: 'Inventario de equipos' },
          { name: 'Respaldos de configuración' },
        ],
      },
      {
        name: 'Seguridad',
        children: [{ name: 'Políticas' }, { name: 'Auditorías' }, { name: 'Gestión de accesos' }],
      },
      { name: 'Soporte y mesa de ayuda' },
      { name: 'Contratos y acuerdos de nivel de servicio' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.TEAMS,
      FEATURE_CODE.FOLDER_SHARING,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'environment',
        label: 'Entorno',
        type: 'select',
        options: ['Desarrollo', 'Pruebas', 'Preproducción', 'Producción'],
        appliesTo: 'FILE',
      },
      {
        key: 'document_type',
        label: 'Tipo de documento',
        type: 'select',
        options: [
          'Requerimiento',
          'Arquitectura',
          'Manual',
          'Plan de pruebas',
          'Acta',
          'Informe',
          'Política',
        ],
        appliesTo: 'FILE',
      },
      { key: 'version', label: 'Versión', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'it_services',
        label: '¿Qué servicios prestas?',
        type: 'multiselect',
        options: [
          { value: 'software', label: 'Desarrollo de software' },
          { value: 'infrastructure', label: 'Infraestructura y redes' },
          { value: 'support', label: 'Soporte y mesa de ayuda' },
          { value: 'security', label: 'Ciberseguridad' },
          { value: 'data', label: 'Datos y BI' },
          { value: 'consulting', label: 'Consultoría TI' },
        ],
      },
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'xlsx', 'md', 'json', 'csv', 'zip', 'png'],
    complianceNote:
      'No guardes contraseñas, claves de API ni certificados privados como archivos sueltos: usa un gestor de secretos y deja aquí sólo la documentación.',
  },
  {
    code: 'ENVIRONMENTAL_HSE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ambiental, seguridad y salud (HSE)',
    description: 'Estudios ambientales, monitoreos, permisos y gestión de seguridad y salud.',
    icon: '🌱',
    sortOrder: 180,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Estudios ambientales',
        children: [
          { name: 'Impacto ambiental' },
          { name: 'Línea base' },
          { name: 'Planes de manejo' },
        ],
      },
      {
        name: 'Monitoreos',
        children: [{ name: 'Agua' }, { name: 'Aire' }, { name: 'Ruido' }, { name: 'Suelos' }],
      },
      { name: 'Permisos y licencias ambientales' },
      {
        name: 'Seguridad y salud en el trabajo',
        children: [
          { name: 'Matriz de riesgos' },
          { name: 'Inspecciones' },
          { name: 'Capacitaciones' },
          { name: 'Investigación de incidentes' },
        ],
      },
      { name: 'Auditorías y hallazgos' },
      { name: 'Informes a la autoridad' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.HISTORY_DAYS,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'site', label: 'Sitio / Instalación', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'permit_number',
        label: 'Número de permiso o licencia',
        type: 'text',
        appliesTo: 'BOTH',
      },
      { key: 'authority', label: 'Autoridad competente', type: 'text', appliesTo: 'FOLDER' },
      { key: 'monitoring_date', label: 'Fecha del monitoreo', type: 'date', appliesTo: 'FILE' },
      {
        key: 'report_type',
        label: 'Tipo de documento',
        type: 'select',
        options: [
          'Estudio',
          'Monitoreo',
          'Informe de cumplimiento',
          'Investigación de incidente',
          'Capacitación',
        ],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'hse_scope',
        label: '¿Qué gestionas?',
        type: 'multiselect',
        options: [
          { value: 'environmental', label: 'Estudios ambientales' },
          { value: 'monitoring', label: 'Monitoreos y mediciones' },
          { value: 'permits', label: 'Permisos y licencias' },
          { value: 'occupational', label: 'Seguridad y salud en el trabajo' },
          { value: 'audits', label: 'Auditorías y certificaciones ISO' },
        ],
      },
      REGULATED_DATA_QUESTION,
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'xlsx', 'docx', 'jpg', 'kmz', 'csv'],
    complianceNote:
      'Los informes ambientales y de seguridad y salud suelen tener plazos legales de conservación y pueden contener datos de salud de trabajadores. Limita el acceso por permisos.',
  },
  {
    code: 'ENGINEERING_GENERAL',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.ENGINEERING,
    name: 'Ingeniería (otras especialidades)',
    description: 'Química, minas, petróleo, agronómica, biomédica, naval, geológica y más.',
    icon: '🛠️',
    sortOrder: 190,
    terminology: {
      workspace: 'Oficina técnica',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Diseño' },
          { name: 'Cálculos y memorias' },
          { name: 'Planos y esquemas' },
          { name: 'Ensayos y laboratorio' },
          { name: 'Informes técnicos' },
        ],
      },
      { name: 'Normativa y estándares' },
      { name: 'Proveedores y equipos' },
      { name: 'Contratos' },
      { name: 'Fotografías de campo' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'FOLDER' },
      { key: 'discipline', label: 'Disciplina', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'phase',
        label: 'Fase',
        type: 'select',
        options: ['Estudio', 'Diseño', 'Ejecución', 'Operación', 'Cierre'],
        appliesTo: 'BOTH',
      },
      { key: 'drawing_revision', label: 'Revisión', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'engineering_discipline',
        label: '¿Cuál es tu especialidad?',
        type: 'select',
        required: true,
        options: [
          { value: 'chemical', label: 'Química' },
          { value: 'mining', label: 'Minas' },
          { value: 'oil_gas', label: 'Petróleo y gas' },
          { value: 'agricultural', label: 'Agronómica / Agrícola' },
          { value: 'biomedical', label: 'Biomédica' },
          { value: 'geological', label: 'Geológica' },
          { value: 'naval', label: 'Naval' },
          { value: 'aeronautical', label: 'Aeronáutica' },
          { value: 'materials', label: 'Materiales' },
          { value: 'food', label: 'Alimentos' },
          { value: 'other', label: 'Otra' },
        ],
        helpText: 'Adaptaremos los campos y las carpetas a tu disciplina.',
      },
      CAD_FILES_QUESTION,
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'dwg', 'xlsx', 'docx', 'csv'],
  },
];
